import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Ticket, Send, X, ExternalLink } from 'lucide-react';

interface TicketRow {
  ticket_id: string;
  tenant_id: string;
  business_name: string;
  created_by_user_id: string;
  requester_name: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  related_table: string | null;
  related_id: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_TABS: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    open: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    closed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${map[status] || map.open}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function TicketThread({ ticket, onClose, onStatusChanged }: { ticket: TicketRow; onClose: () => void; onStatusChanged: () => void }) {
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(ticket.status);
  const [assignToSelf, setAssignToSelf] = useState(false);

  const fetchMessages = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('support_ticket_messages')
      .select('*')
      .eq('ticket_id', ticket.ticket_id)
      .order('created_at', { ascending: true });
    if (!error && data) setMessages(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.ticket_id]);

  const handleReply = async () => {
    if (!reply.trim()) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setSending(true);
    const { error } = await supabase.rpc('post_support_message', {
      p_ticket_id: ticket.ticket_id,
      p_message: reply.trim(),
    });
    if (error) {
      toast.error(error.message || 'Reply bhejne mein error aayi');
    } else {
      setReply('');
      fetchMessages();
      onStatusChanged();
    }
    setSending(false);
  };

  const handleStatusChange = async (newStatus: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    const { error } = await supabase.rpc('admin_update_ticket_status', {
      p_ticket_id: ticket.ticket_id,
      p_status: newStatus,
      p_assign_to_self: assignToSelf,
    });
    if (error) {
      toast.error(error.message);
    } else {
      setStatus(newStatus);
      toast.success('Status updated');
      onStatusChanged();
    }
  };

  const isClosed = status === 'closed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-xl bg-white dark:bg-slate-900 border dark:border-slate-800 shadow-xl">
        <div className="px-6 py-4 border-b dark:border-slate-800 flex items-start justify-between">
          <div>
            <h3 className="font-bold dark:text-white">{ticket.subject}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">{ticket.business_name} • {ticket.requester_name} • {ticket.category}</p>
            {ticket.related_table && (
              <p className="text-xs text-slate-400 mt-1">Related: {ticket.related_table} ({ticket.related_id?.substring(0, 8)}...)</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading && <div className="text-center text-slate-500 text-sm py-8">Loading thread...</div>}
          {!loading && messages.length === 0 && <div className="text-center text-slate-400 text-sm py-8">No messages yet.</div>}
          {!loading && messages.map((m) => (
            <div key={m.id} className={`max-w-[80%] p-3 rounded-lg text-sm ${m.sender_role === 'admin' ? 'ml-auto bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800 dark:text-slate-200'}`}>
              <p>{m.message}</p>
              <p className={`text-[10px] mt-1 ${m.sender_role === 'admin' ? 'text-white/70' : 'text-slate-400'}`}>{new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t dark:border-slate-800 space-y-3">
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-40 h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_TABS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <input type="checkbox" checked={assignToSelf} onChange={(e) => setAssignToSelf(e.target.checked)} /> Assign to me
            </label>
          </div>
          <div className="flex gap-2">
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              disabled={isClosed}
              placeholder={isClosed ? 'Ticket closed hai — reply nahi bheja ja sakta' : 'Reply likhein...'}
              rows={2}
              className="flex-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            />
            <Button onClick={handleReply} disabled={sending || isClosed || !reply.trim()} className="bg-primary hover:bg-primary/90 text-white self-end gap-1">
              <Send size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SupportTicketsView() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState<TicketRow | null>(null);

  const fetchTickets = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_support_tickets', { p_status: statusFilter });
    if (error) {
      toast.error(error.message || 'Failed to load tickets');
    } else {
      setTickets(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h1 className="text-2xl font-bold dark:text-white">Helpdesk & Support</h1>
        <p className="text-slate-500 dark:text-slate-400">Manage merchant issues and platform tickets.</p>
      </div>

      <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg w-fit">
        {STATUS_TABS.map((s) => (
          <Button key={s.value} variant={statusFilter === s.value ? 'default' : 'ghost'} size="sm" onClick={() => setStatusFilter(s.value)}>
            {s.label}
          </Button>
        ))}
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
              <tr>
                <th className="px-6 py-4">Business</th>
                <th className="px-6 py-4">Subject</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Priority</th>
                <th className="px-6 py-4">Related</th>
                <th className="px-6 py-4">Updated</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">Loading tickets...</td></tr>
              )}
              {!loading && tickets.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    <Ticket className="mx-auto mb-2 text-slate-300 dark:text-slate-700" size={28} />
                    No tickets in this status.
                  </td>
                </tr>
              )}
              {!loading && tickets.map((t) => (
                <tr key={t.ticket_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-6 py-4 font-semibold dark:text-white">{t.business_name}</td>
                  <td className="px-6 py-4 dark:text-slate-300">{t.subject}</td>
                  <td className="px-6 py-4 dark:text-slate-300 capitalize">{t.category}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${t.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs text-slate-400">
                    {t.related_table ? (
                      <span className="flex items-center gap-1"><ExternalLink size={12} /> {t.related_table}</span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-slate-500 dark:text-slate-400 text-xs">{new Date(t.updated_at).toLocaleString()}</td>
                  <td className="px-6 py-4 text-right">
                    <Button size="sm" variant="outline" className="dark:border-slate-700 dark:text-white" onClick={() => setActiveTicket(t)}>
                      View & Reply
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {activeTicket && (
        <TicketThread
          ticket={activeTicket}
          onClose={() => setActiveTicket(null)}
          onStatusChanged={fetchTickets}
        />
      )}
    </div>
  );
}

export default SupportTicketsView;
